/**
 * @file     MortgageWidget.tsx
 * @readme   ../README.md
 * @purpose  Embeddable mortgage calculator widget for use inside lessons. Wraps the existing MortgageCalculatorFlow.
 */
'use client';

import dynamic from 'next/dynamic';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import Link from 'next/link';
import { useAppTheme } from '@/core/theme';

const MortgageCalculatorFlow = dynamic(
  () => import('@/features/mortgage-calculator-flow').then((m) => ({ default: m.MortgageCalculatorFlow })),
  { ssr: false, loading: () => <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ color: 'text.secondary' }}>Loading calculator...</Typography></Box> },
);

interface MortgageWidgetProps {
  compact?: boolean;
}

export function MortgageWidget({ compact = false }: MortgageWidgetProps) {
  const { theme } = useAppTheme();

  if (compact) {
    return (
      <Link href="/mortgage-calculator" style={{ textDecoration: 'none', color: 'inherit' }}>
        <Box
          sx={{
            p: 3,
            borderRadius: `${theme.layout.radius.lg}px`,
            border: `1px solid ${theme.semanticColors.borderSubtle}`,
            backgroundColor: alpha(theme.rawSemanticColors.interactivePrimary, 0.04),
            transition: `all ${theme.motion.durations.normal}ms ${theme.motion.easing.standard}`,
            cursor: 'pointer',
            '&:hover': {
              borderColor: theme.semanticColors.interactivePrimary,
              transform: 'translateY(-2px)',
            },
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: theme.semanticColors.textPrimary, mb: 0.5 }}>
            Try the Mortgage Calculator
          </Typography>
          <Typography sx={{ fontSize: 12, color: theme.semanticColors.textSecondary, mb: 1 }}>
            Plug in your numbers to see monthly payments, stress test qualification, and CMHC premiums.
          </Typography>
          <Button
            variant="contained"
            size="small"
            tabIndex={-1}
            sx={{ textTransform: 'none', borderRadius: `${theme.layout.radius.md}px`, fontSize: 12, pointerEvents: 'none' }}
          >
            Open Calculator
          </Button>
        </Box>
      </Link>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: `${theme.layout.radius.lg}px`,
        border: `1px solid ${theme.semanticColors.borderSubtle}`,
        overflow: 'hidden',
        my: 2,
      }}
    >
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.semanticColors.borderSubtle}` }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: theme.semanticColors.textPrimary }}>
          Interactive: Try the Mortgage Calculator
        </Typography>
        <Typography sx={{ fontSize: 12, color: theme.semanticColors.textSecondary }}>
          Apply what you just learned. Adjust the numbers to see how they affect your monthly payment.
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        <MortgageCalculatorFlow sourcePage="academy" />
      </Box>
    </Box>
  );
}
